import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="seasonal-theme-toggle"
export default class extends Controller {
  static outlets = ["theme-switcher"]
  static values = {
    theme: String,
    startMonth: Number,
    startDay: Number,
    endMonth: Number,
    endDay: Number
  }

  connect() {
    this.checkVisibility()
    // Listen for theme changes
    this.boundThemeChange = () => this.checkVisibility()
    document.addEventListener('theme-changed', this.boundThemeChange)
    window.addEventListener('storage', this.boundThemeChange)
  }

  disconnect() {
    document.removeEventListener('theme-changed', this.boundThemeChange)
    window.removeEventListener('storage', this.boundThemeChange)
  }

  checkVisibility() {
    const now = new Date()
    const year = now.getFullYear()
    
    // Use data values for configurable dates (months are 1-based in values, 0-based in JS Date)
    const startMonth = this.startMonthValue - 1
    const startDay = this.startDayValue
    const endMonth = this.endMonthValue - 1
    const endDay = this.endDayValue
    
    let startDate, endDate
    
    // Handle year-spanning seasons (e.g., Dec 17 - Jan 7)
    if (endMonth < startMonth) {
      // Season spans year boundary
      const startThisYear = new Date(year, startMonth, startDay)
      const endNextYear = new Date(year + 1, endMonth, endDay, 23, 59, 59)
      const startLastYear = new Date(year - 1, startMonth, startDay)
      const endThisYear = new Date(year, endMonth, endDay, 23, 59, 59)
      
      // Check if we're in either the end of last year's season or start of this year's
      const inCurrentSeason = now >= startThisYear && now <= endNextYear
      const inPreviousSeason = now >= startLastYear && now <= endThisYear
      
      if (inCurrentSeason || inPreviousSeason) {
        this.showIfNotActive()
        return
      }
    } else {
      // Normal season within same year
      startDate = new Date(year, startMonth, startDay)
      endDate = new Date(year, endMonth, endDay, 23, 59, 59)
      
      if (now >= startDate && now <= endDate) {
        this.showIfNotActive()
        return
      }
    }
    
    // Not in season, hide
    this.element.classList.add("hidden")
  }
  
  showIfNotActive() {
    const currentTheme = this.getCurrentTheme()
    const isThemeActive = currentTheme === this.themeValue
    
    // Show button only when theme is not active
    if (!isThemeActive) {
      this.element.classList.remove("hidden")
    } else {
      this.element.classList.add("hidden")
    }
  }

  activate() {
    // Use outlet if available, otherwise directly set theme
    if (this.hasThemeSwitcherOutlet) {
      // Create a mock event with params
      const mockEvent = { params: { theme: this.themeValue } }
      this.themeSwitcherOutlet.setTheme(mockEvent)
    } else {
      // Fallback: directly set theme
      this.setThemeDirectly(this.themeValue)
    }
    
    // Hide button immediately
    this.element.classList.add("hidden")
  }

  setThemeDirectly(theme) {
    localStorage.setItem("theme", theme)
    document.documentElement.setAttribute("data-theme", theme)
    document.dispatchEvent(new CustomEvent('theme-changed'))
  }

  getCurrentTheme() {
    return localStorage.getItem("theme") || "system"
  }
}
